import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import EmptyState from "../../components/EmptyState";
import api from "../../lib/api";
import {
  Building2,
  Users,
  Award,
  ScrollText,
  TrendingUp,
  Globe2,
  Loader2,
  Download,
} from "lucide-react";

export default function PartnerDashboard() {
  const [partner, setPartner] = useState(null);
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/partners/me")
      .then((r) => {
        setPartner(r.data);
        return Promise.all([
          api.get(`/partners/${r.data.id}/impact`),
          api.get(`/certificates/analytics?partner_id=${r.data.id}`).catch(() => ({ data: null })),
        ]);
      })
      .then(([impactRes, certRes]) => {
        const impact = impactRes.data;
        if (certRes.data) {
          impact.metrics = {
            ...impact.metrics,
            certificates_issued: certRes.data.total,
            awards_won: certRes.data.by_award_title
              ? certRes.data.by_award_title.filter((x) => x.award !== "Participation").reduce((a, x) => a + x.count, 0)
              : impact.metrics.awards_won || 0,
            verified_certificates: certRes.data.verified || 0,
          };
        }
        setImpact(impact);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto py-20 text-center">
        <Loader2 className="h-6 w-6 text-[#0F4C81] animate-spin mx-auto" />
      </div>
    </DashboardLayout>
  );

  if (!partner) return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto py-14">
        <EmptyState
          icon={Building2}
          eyebrow="Partner Institution"
          title="Your partner profile isn't linked yet"
          description="Partner institutions get a dedicated dashboard tracking student outcomes, certificate analytics and event impact across every Diplomexa conference. Get in touch to activate yours."
          primary={{ label: "Talk to a Diplomexa specialist", onClick: () => window.location.href = "mailto:info@factskoot.com?subject=Partner%20activation" }}
          secondary={{ label: "Back", onClick: () => window.history.back() }}
          testId="partner-empty"
        />
      </div>
    </DashboardLayout>
  );

  const m = impact?.metrics || {};

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto pb-16">
        <div className="rounded-2xl bg-[#0A192F] text-white p-8 lg:p-10 border border-[#D4AF37]/30 relative overflow-hidden" data-testid="partner-hero">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#D4AF37]/10 blur-3xl" />
          <div className="relative">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Partner Institution</p>
            <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tighter">{partner.name}</h1>
            <p className="mt-2 text-slate-300 text-sm">{partner.description}</p>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-[#D4AF37] text-[#0A192F] text-xs font-bold uppercase tracking-[0.2em]" data-testid="partner-type">
                {partner.type}
              </span>
              <span className="text-xs text-slate-400 inline-flex items-center gap-1">
                <Globe2 className="h-3 w-3" /> {partner.country || "—"}
              </span>
              {partner.referral_code && (
                <span className="text-xs text-slate-400">Referral code: <span className="font-mono text-[#D4AF37]">{partner.referral_code}</span></span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="partner-metrics">
          <Metric icon={Users} label="Delegates referred" value={m.delegates_referred || 0} accent="text-[#0F4C81]" />
          <Metric icon={ScrollText} label="Certificates issued" value={m.certificates_issued || 0} accent="text-emerald-600" />
          <Metric icon={TrendingUp} label="Engagement score" value={`${m.engagement_score || 0}/100`} accent="text-[#D4AF37]" />
          <Metric icon={Award} label="Co-branded events" value={partner.co_branded_count || 0} accent="text-[#0A192F]" />
        </div>

        <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Impact Report</p>
              <h2 className="mt-1 text-xl font-semibold tracking-tight text-[#0A192F]">Global exposure summary</h2>
            </div>
            <button className="text-xs uppercase tracking-[0.2em] font-bold text-[#0F4C81] inline-flex items-center gap-1.5">
              <Download className="h-3.5 w-3.5" /> Download report
            </button>
          </div>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <ImpactRow label="Network size" value={`${(partner.delegates_referred || 0) * 5}+ students`} />
            <ImpactRow label="Conferences participated" value={`${(partner.delegates_referred || 0) > 0 ? 4 : 0}`} />
            <ImpactRow label="Awards won (network)" value={`${Math.round((partner.delegates_referred || 0) * 0.3)}`} />
            <ImpactRow label="Avg leadership growth" value="+18%" />
          </div>
        </div>

        <div className="mt-8 rounded-2xl bg-gradient-to-br from-[#0A192F] to-[#0F4C81] text-white p-7 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Partner positioning</p>
            <p className="mt-2 text-xl font-serif italic">"Turn partner institutions into long-term ecosystem collaborators."</p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <Icon className={`h-4 w-4 ${accent}`} />
      <p className={`mt-3 font-serif text-3xl ${accent}`}>{value}</p>
      <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</p>
    </div>
  );
}

function ImpactRow({ label, value }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-md bg-slate-50">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="font-semibold text-[#0A192F] text-sm">{value}</span>
    </div>
  );
}
